import React, { useEffect, useState, useRef, useCallback, useImperativeHandle } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchPosts } from './postsActions'
import { swipePost, undoSwipe } from '../Swipe/swipeActions'
import usePostsWebSocket from '../../hooks/usePostsWebSocket'
import {
  Box, Typography, CircularProgress, Alert, Card,
  Button, Chip, Container, IconButton, Avatar
} from '@mui/material'
import AppointmentDialog from './AppointmentDialog'
import {
  Image as ImageIcon,
  LocalParking as ParkingIcon,
  Pets as PetIcon,
  Pool as PoolIcon,
  LocationOn as LocationIcon,
  Hotel as BedIcon,
  Bathtub as BathIcon,
  SquareFoot as AreaIcon,
  Refresh as RefreshIcon,
  Event as EventIcon,
  Close as CloseIcon,
  Favorite as FavoriteIcon,
  Replay as RewindIcon,
  CheckCircleRounded as CheckCircleRoundedIcon,
  Tune as TuneIcon
} from '@mui/icons-material'
import BottomNavBar from '../../components/BottomNavBar'
import ImageLightbox from '../../components/ImageLightbox'
import ManagePreferencesDialog from '../../components/ManagePreferencesDialog'

// ─── constants ───────────────────────────────────────────────────────────────

const SWIPE_THRESHOLD    = 90   // px horizontal to commit a swipe
const VELOCITY_THRESHOLD = 0.4  // px/ms — fast flick also commits
const DIR_LOCK_PX        = 8    // px movement before we lock gesture direction
const BOTTOM_NAV         = 64   // height of the bottom nav bar in px
const EXIT_MS            = 280  // fixed exit-animation duration
const HEADER_H           = 56   // height of the "SwipeeIT" header bar in px
const CARD_MARGIN        = 12   // gap between the card stack and the screen edges
const CARD_RADIUS        = 1.5  // border-radius units (theme spacing) for the card

// ─── ExploreHeader ────────────────────────────────────────────────────────────
// Fixed brand bar above the card stack. The right-hand icon opens the house
// preferences dialog directly — the closest thing this app has to a
// feed-filter setting, so it's a real action rather than a decorative icon.

function ExploreHeader({ onOpenPreferences }) {
  return (
    <Box sx={{
      position: 'absolute', top: 0, left: 0, right: 0, zIndex: 60,
      height: `calc(${HEADER_H}px + env(safe-area-inset-top, 0px))`,
      pt: 'env(safe-area-inset-top, 0px)',
      bgcolor: 'background.paper',
      borderBottom: '1px solid', borderColor: 'divider',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      px: 2.5,
    }}>
      <Typography sx={{ fontWeight: 900, fontSize: '1.35rem', letterSpacing: '-0.5px', color: 'primary.main' }}>
        SwipeeIT
      </Typography>
      <IconButton onClick={onOpenPreferences} aria-label="Manage house preferences" sx={{ color: 'text.secondary' }}>
        <TuneIcon />
      </IconButton>
    </Box>
  )
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <Box sx={{ bgcolor: 'background.paper', borderRadius: 1.5, p: 2, mb: 1.5 }}>
      {title && (
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          {title}
        </Typography>
      )}
      {children}
    </Box>
  )
}

function formatCAD(value) {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency', currency: 'CAD', maximumFractionDigits: 0,
  }).format(value)
}

// Consistent fallback avatar color based on name, same approach as Profile.
function stringToColor(string = '') {
  let hash = 0
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash)
  }
  let color = '#'
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff
    color += `00${Math.max(50, Math.min(200, value % 200)).toString(16)}`.slice(-2)
  }
  return color
}

// ─── PostCard ─────────────────────────────────────────────────────────────────
// Pure display component — no gesture logic. Reused for both the active card
// and the background (next) card.

function PostCard({ post }) {
  const [lightboxOpen,  setLightboxOpen]  = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const openLightbox = (i) => { setLightboxIndex(i); setLightboxOpen(true) }

  return (
    <Card sx={{
      height: '100%',
      borderRadius: CARD_RADIUS,
      overflowY: 'auto',
      overflowX: 'hidden',
      bgcolor: 'background.default',
      boxShadow: 'none',
      userSelect: 'none',
    }}>
      {/* Hero image — fills the card's own height initially (the card's
          height is already the full available space below the header and
          above the nav bar, set by its positioned container) */}
      <Box sx={{ position: 'relative', height: '100%', flexShrink: 0 }}>
        {post.main_image ? (
          <Box
            component="img"
            src={post.main_image}
            alt={post.title}
            draggable={false}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <Box sx={{ width: '100%', height: '100%', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ImageIcon sx={{ fontSize: 72, color: 'action.disabled' }} />
          </Box>
        )}

        {/* Listed-by pill — who the broker/seller is, visible without scrolling */}
        {post.posted_by && (
          <Box sx={{
            position: 'absolute', top: 12, left: 12, zIndex: 5,
            display: 'flex', alignItems: 'center', gap: 0.75,
            bgcolor: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)',
            borderRadius: 10, pl: 0.5, pr: 1.25, py: 0.5, maxWidth: '75%',
          }}>
            <Avatar
              src={post.posted_by.profile_picture || undefined}
              sx={{ width: 26, height: 26, fontSize: '0.75rem', bgcolor: stringToColor(post.posted_by.name) }}
            >
              {post.posted_by.name?.[0]?.toUpperCase() || '?'}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.15 }} noWrap>
                {post.posted_by.name}
              </Typography>
              {post.posted_by.user_type && (
                <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.6rem', lineHeight: 1.15 }} noWrap>
                  {post.posted_by.user_type}
                </Typography>
              )}
            </Box>
            {post.posted_by.is_verified && (
              <CheckCircleRoundedIcon sx={{ fontSize: 14, color: '#4fc3f7', flexShrink: 0 }} />
            )}
          </Box>
        )}

        {/* Gradient overlay with key info — sits above the floating buttons */}
        <Box sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          pt: 16, pb: 14, px: 2.5,
          pointerEvents: 'none',
          background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.48) 50%, transparent 100%)',
        }}>
          <Typography variant="h4" fontWeight={800} sx={{ color: '#fff', lineHeight: 1.2, mb: 0.25 }} noWrap>
            {post.title}
          </Typography>
          <Typography variant="h6" fontWeight={600} sx={{ color: 'rgba(255,255,255,0.92)', mb: 0.5 }}>
            {formatCAD(post.price)}
          </Typography>
          {[post.city, post.province].filter(Boolean).length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LocationIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', mr: 0.5 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem' }}>
                {[post.city, post.province].filter(Boolean).join(', ')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Scrollable detail sections revealed by scrolling */}
      <Box sx={{ p: 2, pb: 16, bgcolor: 'background.default' }}>
        <Section title="Property Details">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {[
              { icon: <BedIcon />,  label: `${post.bedrooms ?? 0} Bedrooms` },
              { icon: <BathIcon />, label: `${post.bathrooms ? Math.floor(post.bathrooms) : 0} Bathrooms` },
              { icon: <AreaIcon />, label: `${post.square_feet ?? 0} sqft` },
            ].map(({ icon, label }) => (
              <Chip key={label} icon={icon} label={label} size="small" variant="outlined" sx={{ borderRadius: 1.5 }} />
            ))}
          </Box>
        </Section>

        {post.description && (
          <Section title="About this property">
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
              {post.description}
            </Typography>
          </Section>
        )}

        {(post.has_garage || post.is_pet_friendly || post.has_pool || post.preferences?.length > 0) && (
          <Section title="Amenities">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {post.has_garage     && <Chip icon={<ParkingIcon />} label="Parking"     size="small" variant="outlined" sx={{ borderRadius: 1.5 }} />}
              {post.is_pet_friendly && <Chip icon={<PetIcon />}    label="Pet Friendly" size="small" variant="outlined" sx={{ borderRadius: 1.5 }} />}
              {post.has_pool       && <Chip icon={<PoolIcon />}    label="Pool"         size="small" variant="outlined" sx={{ borderRadius: 1.5 }} />}
              {post.preferences?.map((p, i) => (
                <Chip key={`${p}-${i}`} label={p} size="small"
                  sx={{ fontSize: '0.7rem', bgcolor: 'rgba(25,118,210,0.08)', color: 'primary.dark', borderRadius: 1.5 }} />
              ))}
            </Box>
          </Section>
        )}

        {post.additional_images?.length > 0 && (
          <Section title="More Photos">
            <Box sx={{
              display: 'flex', gap: 1, overflowX: 'auto',
              pb: 0.5,
              '&::-webkit-scrollbar': { height: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
            }}>
              {post.additional_images.map((img, i) => (
                <Box
                  key={img.id ?? i}
                  component="img"
                  src={img.image}
                  alt={img.description || 'Property photo'}
                  draggable={false}
                  onClick={() => openLightbox(i)}
                  sx={{
                    height: 110, width: 160, flexShrink: 0,
                    objectFit: 'cover', borderRadius: 1.5,
                    cursor: 'pointer',
                    transition: 'opacity 0.15s',
                    '&:hover': { opacity: 0.85 },
                  }}
                />
              ))}
            </Box>
          </Section>
        )}
      </Box>

      <ImageLightbox
        images={post.additional_images ?? []}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </Card>
  )
}

// ─── SwipeCard ────────────────────────────────────────────────────────────────
// Handles all gesture detection and animation. Exposes triggerSwipe() via ref.
//
// Gesture rules
//   • First DIR_LOCK_PX px of movement decide horizontal (swipe) or vertical (scroll).
//   • Horizontal: preventDefault, track the finger 1:1, commit or spring-back on release.
//   • Vertical: pass through so PostCard can scroll naturally.
//
// While dragging, nothing goes through React state — every frame writes
// straight to the DOM via refs. Re-rendering this tree on every pixel of
// movement (which is what state-driven styling means MUI recomputes) is the
// main reason the old version felt bad everywhere: WebViews in particular
// don't have the headroom to do that at 60fps. React only re-renders here
// for the rare, discrete transitions (entrance, exit, spring-back), not for
// the drag itself. memo() guards against an unrelated parent re-render
// (e.g. a websocket tick) resetting the card's position mid-gesture.

const SwipeCard = React.memo(React.forwardRef(function SwipeCard({ post, onSwipe, enterFrom }, ref) {
  const [leaving, setLeaving] = useState(null)  // null | 'left' | 'right'
  const [ready,   setReady]   = useState(false) // entrance animation gate

  // All mutable gesture state lives in a single ref — zero re-renders from tracking.
  const g = useRef({ active: false, startX: 0, startY: 0, startTime: 0, currentX: 0, dir: null })
  const elRef   = useRef(null)
  const likeRef = useRef(null)
  const nopeRef = useRef(null)
  const rafId   = useRef(null)
  // Snapshot once at mount — this card only ever enters from off-screen when
  // it's being restored by "rewind"; later prop changes must not retrigger it.
  const enterFromRef = useRef(enterFrom)

  // Pop-forward entrance: mount at background-card scale, animate to full on next frame.
  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true))
    return () => cancelAnimationFrame(id)
  }, [])

  // ── Imperative drag rendering ───────────────────────────────────────────────

  const applyDragStyle = (dx) => {
    if (elRef.current) {
      elRef.current.style.transform = `translate3d(${dx}px, 0, 0) rotate(${dx * 0.05}deg)`
    }
    if (likeRef.current) likeRef.current.style.opacity = Math.min(Math.max(dx / SWIPE_THRESHOLD, 0) * 1.6, 1)
    if (nopeRef.current) nopeRef.current.style.opacity = Math.min(Math.max(-dx / SWIPE_THRESHOLD, 0) * 1.6, 1)
  }

  const resetDragStyle = (animated) => {
    if (elRef.current) {
      elRef.current.style.transition = animated ? 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'none'
      elRef.current.style.transform = 'translate3d(0, 0, 0) rotate(0deg)'
    }
    if (likeRef.current) likeRef.current.style.opacity = 0
    if (nopeRef.current) nopeRef.current.style.opacity = 0
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  const exit = useCallback((dir) => {
    setLeaving(dir)
    navigator.vibrate?.(15)
    // Let the exit animation play out then notify the parent.
    setTimeout(() => onSwipe(post.id, dir), EXIT_MS)
  }, [post.id, onSwipe])

  useImperativeHandle(ref, () => ({ triggerSwipe: exit }), [exit])

  const settle = useCallback(() => {
    const { currentX, startTime } = g.current
    const velocity = Math.abs(currentX) / Math.max(Date.now() - startTime, 1)
    if (Math.abs(currentX) > SWIPE_THRESHOLD || velocity > VELOCITY_THRESHOLD) {
      exit(currentX > 0 ? 'right' : 'left')
    } else {
      // Spring back to center
      resetDragStyle(true)
    }
    g.current.active = false
    g.current.dir = null
  }, [exit])

  // ── Touch handling (non-passive so we can preventDefault on horizontal) ────

  useEffect(() => {
    const el = elRef.current
    if (!el) return

    const onTouchMove = (e) => {
      if (!g.current.active) return
      const t = e.touches[0]
      const dx = t.clientX - g.current.startX
      const dy = t.clientY - g.current.startY

      // Lock direction once we've moved enough to be certain.
      if (!g.current.dir && Math.hypot(dx, dy) > DIR_LOCK_PX) {
        g.current.dir = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v'
      }

      if (g.current.dir === 'h') {
        e.preventDefault() // stop browser scroll on horizontal drag
        g.current.currentX = dx
        if (rafId.current) cancelAnimationFrame(rafId.current)
        rafId.current = requestAnimationFrame(() => applyDragStyle(dx))
      }
      // dir === 'v' → do nothing, let the browser scroll PostCard
    }

    el.addEventListener('touchmove', onTouchMove, { passive: false })
    return () => {
      el.removeEventListener('touchmove', onTouchMove)
      if (rafId.current) cancelAnimationFrame(rafId.current)
    }
  }, [])

  // ── Shared start / end ─────────────────────────────────────────────────────

  const handleStart = (clientX, clientY) => {
    if (leaving) return
    if (elRef.current) elRef.current.style.transition = 'none'
    g.current = { active: true, startX: clientX, startY: clientY, startTime: Date.now(), currentX: 0, dir: null }
  }

  const handleEnd = () => {
    if (!g.current.active) return
    if (g.current.dir === 'h') {
      settle()
    } else {
      g.current.active = false
      g.current.dir = null
    }
  }

  // ── Mouse drag (desktop) ───────────────────────────────────────────────────

  const handleMouseMove = (e) => {
    if (!g.current.active || leaving) return
    const dx = e.clientX - g.current.startX
    const dy = e.clientY - g.current.startY
    if (!g.current.dir && Math.hypot(dx, dy) > DIR_LOCK_PX) {
      g.current.dir = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v'
    }
    if (g.current.dir === 'h') {
      g.current.currentX = dx
      applyDragStyle(dx)
    }
  }

  // ── Transform for the discrete, React-driven states (entrance / exit / at rest) ──
  // The drag itself never reaches here — it's handled entirely by the
  // imperative functions above.

  let transform, transition, opacity
  if (!ready && enterFromRef.current) {
    // Rewind: slide back in from the side it originally exited toward.
    const tx = enterFromRef.current === 'right' ? '130vw' : '-130vw'
    const r  = enterFromRef.current === 'right' ? 22 : -22
    transform  = `translate3d(${tx}, 0, 0) rotate(${r}deg)`
    transition = 'none'
    opacity    = 1
  } else if (leaving) {
    const tx = leaving === 'right' ? '130vw' : '-130vw'
    const r  = leaving === 'right' ? 22 : -22
    transform  = `translate3d(${tx}, 0, 0) rotate(${r}deg)`
    transition = `transform ${EXIT_MS}ms ease-out, opacity 0.2s ease`
    opacity    = 0
  } else {
    transform  = 'translate3d(0, 0, 0) rotate(0deg)'
    transition = 'none'
    opacity    = 1
  }

  return (
    <Box
      ref={elRef}
      sx={{
        position: 'absolute', inset: 0, zIndex: 10,
        // Rotation pivot near the bottom — feels like holding a physical card.
        transformOrigin: '50% 80%',
        willChange: 'transform',
        cursor: 'grab',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        boxShadow: '0 10px 28px rgba(0,0,0,0.18)',
        borderRadius: CARD_RADIUS,
      }}
      style={{ transform, transition, opacity }}
      onMouseDown={(e) => { if (e.button === 0) handleStart(e.clientX, e.clientY) }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
      onTouchStart={(e) => handleStart(e.touches[0].clientX, e.touches[0].clientY)}
      onTouchEnd={handleEnd}
    >
      {/* LIKE stamp — fades in as card is dragged right */}
      <Box ref={likeRef} sx={{
        position: 'absolute', top: 40, left: 20, zIndex: 20, pointerEvents: 'none',
        opacity: 0, transform: 'rotate(-20deg)',
        border: '4px solid #21d07a', borderRadius: 1, px: 1.5, py: 0.5,
      }}>
        <Typography sx={{ color: '#21d07a', fontWeight: 900, fontSize: '1.6rem', letterSpacing: 4, lineHeight: 1 }}>
          LIKE
        </Typography>
      </Box>

      {/* NOPE stamp — fades in as card is dragged left */}
      <Box ref={nopeRef} sx={{
        position: 'absolute', top: 40, right: 20, zIndex: 20, pointerEvents: 'none',
        opacity: 0, transform: 'rotate(20deg)',
        border: '4px solid #fd5068', borderRadius: 1, px: 1.5, py: 0.5,
      }}>
        <Typography sx={{ color: '#fd5068', fontWeight: 900, fontSize: '1.6rem', letterSpacing: 4, lineHeight: 1 }}>
          NOPE
        </Typography>
      </Box>

      <PostCard post={post} />
    </Box>
  )
}))

// ─── Posts ────────────────────────────────────────────────────────────────────

export default function Posts() {
  const dispatch = useDispatch()
  const { posts, loading, error } = useSelector(s => s.posts || {})
  const { isAuthenticated }       = useSelector(s => s.auth  || {})

  const [allPosts,        setAllPosts]        = useState([])
  const [currentIndex,   setCurrentIndex]    = useState(0)
  const [initialized,    setInitialized]     = useState(false)
  const [wsLoading,      setWsLoading]       = useState(false)
  const [hasMore,        setHasMore]         = useState(true)
  const [currentPage,    setCurrentPage]     = useState(1)
  const [appointmentOpen, setAppointmentOpen] = useState(false)
  const [preferencesOpen, setPreferencesOpen] = useState(false)
  const [rewindDir,      setRewindDir]        = useState(null) // direction the rewound card should re-enter from
  const [announcement,   setAnnouncement]     = useState('')   // aria-live status text

  const cardRef    = useRef(null)
  // Posts shown this session — cleared on Start Over.
  const seenIds    = useRef(new Set())
  // Posts the user has liked (right-swiped) this session.
  // Never cleared, even on Start Over, so liked posts can't reappear
  // due to race conditions where the swipe API call hasn't completed yet.
  const likedIds   = useRef(new Set())
  const restBatchRef = useRef(null)
  const fetchedRef = useRef(false)

  // ── Merge helpers ────────────────────────────────────────────────────────

  // Appends only truly new posts (by ID) and returns the count added.
  // Filters out both seen posts and liked posts (double guard).
  const mergePosts = useCallback((incoming) => {
    const fresh = incoming.filter(p =>
      !seenIds.current.has(p.id) && !likedIds.current.has(p.id)
    )
    if (!fresh.length) return 0
    fresh.forEach(p => seenIds.current.add(p.id))
    setAllPosts(prev => [...prev, ...fresh])
    return fresh.length
  }, [])

  // ── WebSocket callback ───────────────────────────────────────────────────

  const handleNewPosts = useCallback((data, type) => {
    const isInitial = type === 'posts_initial'

    if (data.posts?.length > 0) {
      const added = mergePosts(data.posts)
      // For load-more responses, zero new posts = feed exhausted.
      // For reconnect initials, zero new posts just means we already have them.
      if (added === 0 && !isInitial) setHasMore(false)
      else if (added > 0)            setHasMore(true)
      setInitialized(true)
    } else if (!isInitial) {
      // Empty load-more response — nothing left.
      setHasMore(false)
      setInitialized(true)
    }

    if (!isInitial) setWsLoading(false)
    if (data.pagination) setCurrentPage(data.pagination.current_page)
  }, [mergePosts])

  const { wsConnected, notifyReachedEnd } = usePostsWebSocket(isAuthenticated, handleNewPosts)

  // ── Initial REST load ────────────────────────────────────────────────────

  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true
      dispatch(fetchPosts(1))
      setInitialized(true)
    }
  }, [dispatch])

  // Merge Redux REST posts into allPosts via the same seenIds dedup.
  // Guard on `loading` so stale Redux data from a previous fetch doesn't get
  // merged when Start Over fires a new REST request (loading=true means
  // the response hasn't arrived yet — the current `posts` value is stale).
  //
  // This is also the REST fallback's exhaustion signal: the websocket path
  // flips wsLoading/hasMore off itself (see handleNewPosts), but when the
  // load-more effect falls back to REST (wsConnected was false), nothing
  // else ever clears wsLoading — so an empty response here must do it,
  // the same way handleNewPosts does for the websocket path.
  useEffect(() => {
    if (!initialized || loading || !posts) return
    const batchKey = posts.length > 0 ? posts[0].id : 'empty'
    if (batchKey === restBatchRef.current) return
    restBatchRef.current = batchKey
    if (posts.length > 0) {
      mergePosts(posts)
    } else if (currentPage > 1) {
      // Ran out of pages via the REST fallback — mirrors an empty
      // websocket load-more batch meaning the feed is exhausted.
      setHasMore(false)
    }
    setWsLoading(false)
  }, [posts, initialized, loading, mergePosts, currentPage])

  // ── Load-more trigger ────────────────────────────────────────────────────

  useEffect(() => {
    if (!initialized || wsLoading || !hasMore) return
    if (allPosts.length > 0 && currentIndex >= allPosts.length - 2) {
      setWsLoading(true)
      const next = currentPage + 1
      setCurrentPage(next)
      if (wsConnected) notifyReachedEnd(next)
      else             dispatch(fetchPosts(next))
    }
  }, [currentIndex, allPosts.length, wsLoading, hasMore, currentPage, wsConnected, initialized, notifyReachedEnd, dispatch])

  // ── Swipe handler ────────────────────────────────────────────────────────

  const handleSwipe = useCallback(async (postId, direction) => {
    if (!postId) return
    // Record liked posts immediately in-memory so they can't reappear
    // if Start Over is clicked before the API call completes.
    if (direction === 'right') likedIds.current.add(postId)
    try {
      await dispatch(swipePost(postId, direction))
      setCurrentIndex(prev => prev + 1)
      setAnnouncement(direction === 'right' ? 'Liked' : direction === 'left' ? 'Passed' : 'Swiped')
    } catch {
      setWsLoading(false)
    }
  }, [dispatch])

  // ── Rewind (undo last swipe) ────────────────────────────────────────────

  const handleRewind = useCallback(async () => {
    if (currentIndex === 0) return
    const prevPost = allPosts[currentIndex - 1]
    if (!prevPost) return
    try {
      const result = await dispatch(undoSwipe())
      // Liked posts are tracked forever so they can't reappear via merge —
      // undo it here so a re-swipe on the restored card behaves normally.
      if (result?.direction === 'right') likedIds.current.delete(prevPost.id)
      setRewindDir(result?.direction === 'left' ? 'left' : 'right')
      setCurrentIndex(prev => prev - 1)
      setAnnouncement('Rewound to previous listing')
    } catch {
      // Nothing to undo (fresh session) or the request failed — no-op.
    }
  }, [dispatch, currentIndex, allPosts])

  // ── Refresh ──────────────────────────────────────────────────────────────

  const handleRefresh = () => {
    // Reset the seen-posts set but keep likedIds intact so liked posts
    // can never reappear even if their swipe API call races with this refresh.
    seenIds.current = new Set(likedIds.current)
    // Snapshot the current first-post ID so the REST merge useEffect
    // skips the still-cached Redux batch until the new response arrives.
    restBatchRef.current = posts?.[0]?.id ?? null
    fetchedRef.current   = true
    setCurrentIndex(0)
    setAllPosts([])
    setHasMore(true)
    setCurrentPage(1)
    setWsLoading(false)
    dispatch(fetchPosts(1))
    setInitialized(true)
  }

  // ── Derived ──────────────────────────────────────────────────────────────

  const currentPost   = allPosts[currentIndex]
  const nextPost      = allPosts[currentIndex + 1]
  const nextNextPost  = allPosts[currentIndex + 2]

  // Preload the next couple of images so advancing the deck never pops in.
  useEffect(() => {
    [nextPost, nextNextPost].forEach(p => {
      if (p?.main_image) {
        const img = new window.Image()
        img.src = p.main_image
      }
    })
  }, [nextPost, nextNextPost])

  // rewindDir only applies to the very next SwipeCard mount — clear it once
  // the deck has moved on so a later forward swipe doesn't reuse it.
  useEffect(() => {
    if (rewindDir) setRewindDir(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPost?.id])

  // ── Keyboard navigation (desktop + accessibility) ────────────────────────
  // Arrows mirror the on-screen buttons; Cmd/Ctrl+Z rewinds like an undo.

  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = document.activeElement?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || appointmentOpen) return

      if ((e.key === 'z' || e.key === 'Z') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        handleRewind()
        return
      }
      if (!currentPost) return
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        cardRef.current?.triggerSwipe('left')
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        cardRef.current?.triggerSwipe('right')
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setAppointmentOpen(true)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [currentPost, appointmentOpen, handleRewind])

  // ── Render states ────────────────────────────────────────────────────────

  if (loading && !initialized) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
  }

  if (!currentPost && !loading && !wsLoading) {
    return (
      <Container maxWidth="sm" sx={{
        height: '100vh', position: 'relative', display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: 3,
        pt: `calc(${HEADER_H}px + env(safe-area-inset-top, 0px) + 24px)`,
      }}>
        <ExploreHeader onOpenPreferences={() => setPreferencesOpen(true)} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {!hasMore ? "You've seen everything!" : 'No posts available'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {!hasMore ? 'Check back later for new listings.' : 'No posts match your preferences right now.'}
        </Typography>
        <Button variant="contained" onClick={handleRefresh} startIcon={<RefreshIcon />}
          sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none' }}>
          Start Over
        </Button>
        <ManagePreferencesDialog open={preferencesOpen} onClose={() => setPreferencesOpen(false)} />
        <BottomNavBar />
      </Container>
    )
  }

  // ── Main render ──────────────────────────────────────────────────────────

  return (
    <Box sx={{ height: '100vh', position: 'relative', overflow: 'hidden', bgcolor: 'background.default' }}>

      <ExploreHeader onOpenPreferences={() => setPreferencesOpen(true)} />

      {/* Screen-reader announcement of the last action — the gesture itself is silent to a11y tools */}
      <Box aria-live="polite" sx={{
        position: 'absolute', width: 1, height: 1, overflow: 'hidden',
        clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap',
      }}>
        {announcement}
      </Box>

      {/* Card stack — inset from the screen edges with rounded corners,
          sitting below the header and above the bottom nav */}
      <Box sx={{
        position: 'absolute',
        top: `calc(${HEADER_H}px + env(safe-area-inset-top, 0px) + ${CARD_MARGIN}px)`,
        left: CARD_MARGIN,
        right: CARD_MARGIN,
        bottom: `calc(${BOTTOM_NAV}px + env(safe-area-inset-bottom, 0px) + ${CARD_MARGIN}px)`,
      }}>

        {/* Third card — deepest in the stack, just a hint of what's coming.
            Identical position and size to the front card — promoting it
            later is a pure opacity/z-index change, no movement at all. */}
        {nextNextPost && (
          <Box
            sx={{
              position: 'absolute', inset: 0, zIndex: 4,
              opacity: 0.5,
              pointerEvents: 'none',
              willChange: 'opacity',
            }}
          >
            <PostCard post={nextNextPost} />
          </Box>
        )}

        {/* Background card — a fixed-position peek of the next post */}
        {nextPost && (
          <Box
            sx={{
              position: 'absolute', inset: 0, zIndex: 5,
              opacity: 0.72,
              pointerEvents: 'none',
              willChange: 'opacity',
            }}
          >
            <PostCard post={nextPost} />
          </Box>
        )}

        {/* Active (foreground) card */}
        {currentPost && (
          <SwipeCard
            key={currentPost.id}
            post={currentPost}
            onSwipe={handleSwipe}
            enterFrom={rewindDir}
            ref={cardRef}
          />
        )}

        {/* Spinner while waiting for next batch */}
        {wsLoading && !currentPost && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        )}

        {/* Floating action buttons */}
        {currentPost && (
          <Box sx={{
            position: 'absolute', bottom: 20, left: 0, right: 0, zIndex: 50,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2.5,
            pointerEvents: 'none',
          }}>
            {/* Rewind — undo the last swipe, like Tinder/Bumble's backtrack */}
            <IconButton
              onClick={handleRewind}
              disabled={currentIndex === 0}
              aria-label="Rewind to previous listing"
              sx={{
                pointerEvents: 'auto', width: 48, height: 48,
                bgcolor: 'background.paper',
                boxShadow: currentIndex === 0 ? 'none' : '0 4px 14px rgba(255,196,61,0.4)',
                border: '2px solid #ffc83d', color: '#ffc83d',
                opacity: currentIndex === 0 ? 0.35 : 1,
                transition: 'all 0.18s',
                '&:hover': currentIndex === 0 ? {} : { bgcolor: 'rgba(255,196,61,0.1)', transform: 'scale(1.1)' },
                '&.Mui-disabled': { border: '2px solid #ffc83d', color: '#ffc83d' },
              }}
            >
              <RewindIcon sx={{ fontSize: 22 }} />
            </IconButton>

            {/* Dislike */}
            <IconButton
              onClick={() => cardRef.current?.triggerSwipe('left')}
              aria-label="Pass on this listing"
              sx={{
                pointerEvents: 'auto', width: 64, height: 64,
                bgcolor: 'background.paper',
                boxShadow: '0 4px 18px rgba(253,80,104,0.4)',
                border: '2.5px solid #fd5068', color: '#fd5068',
                transition: 'all 0.18s',
                '&:hover': { bgcolor: 'rgba(253,80,104,0.1)', transform: 'scale(1.1)' },
              }}
            >
              <CloseIcon sx={{ fontSize: 32 }} />
            </IconButton>

            {/* Appointment */}
            <IconButton
              onClick={() => setAppointmentOpen(true)}
              aria-label="Schedule an appointment for this listing"
              sx={{
                pointerEvents: 'auto', width: 64, height: 64,
                bgcolor: 'background.paper',
                boxShadow: '0 4px 18px rgba(66,165,245,0.4)',
                border: '2.5px solid #42a5f5', color: '#42a5f5',
                transition: 'all 0.18s',
                '&:hover': { bgcolor: 'rgba(66,165,245,0.1)', transform: 'scale(1.1)' },
              }}
            >
              <EventIcon sx={{ fontSize: 32 }} />
            </IconButton>

            {/* Like */}
            <IconButton
              onClick={() => cardRef.current?.triggerSwipe('right')}
              aria-label="Like this listing"
              sx={{
                pointerEvents: 'auto', width: 64, height: 64,
                bgcolor: 'background.paper',
                boxShadow: '0 4px 18px rgba(33,208,122,0.4)',
                border: '2.5px solid #21d07a', color: '#21d07a',
                transition: 'all 0.18s',
                '&:hover': { bgcolor: 'rgba(33,208,122,0.1)', transform: 'scale(1.1)' },
              }}
            >
              <FavoriteIcon sx={{ fontSize: 32 }} />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Appointment dialog */}
      {currentPost && (
        <AppointmentDialog
          open={appointmentOpen}
          onClose={() => setAppointmentOpen(false)}
          swipeId={currentPost.id}
          onSuccess={() => {
            setAppointmentOpen(false)
            cardRef.current?.triggerSwipe('right')
          }}
        />
      )}

      <ManagePreferencesDialog open={preferencesOpen} onClose={() => setPreferencesOpen(false)} />

      <BottomNavBar />
    </Box>
  )
}
